<template>
  <div>
    <div id="name-greeting-container">
      <h2 class="form-section-header mb-0">Name Greeting</h2>
      <el-tag class="mt-5">
        <i class="el-icon-info" style="margin-right: 4px" />
        This plays to identify your extension
      </el-tag>
      <voicemail-greeting
        :mailboxuser="mailboxuser"
        :mailboxContext="mailboxContext"
        :currentGreeting="nameGreeting"
        defaultName="name_greeting.wav"
        recordedName="name_greeting.webm"
        dirFolder="greeting"
        :mediafileOptions="mediafileOptions"
        @greetingSaved="updateNameGreeting"
        @greetingRemoved="removeNameGreeting"
      />
    </div>

    <div id="unavailable-greeting-container" class="mt-20">
      <h2 class="form-section-header mb-0">Unavailable Greeting</h2>
      <el-tag class="mt-5">
        <i class="el-icon-info" style="margin-right: 4px" />
        This plays when you do not answer
      </el-tag>
      <voicemail-greeting
        :mailboxuser="mailboxuser"
        :mailboxContext="mailboxContext"
        :currentGreeting="unavailGreeting"
        defaultName="unavail_greeting.wav"
        recordedName="unavail_greeting.webm"
        dirFolder="unavailable"
        :mediafileOptions="mediafileOptions"
        @greetingSaved="updateUnavailGreeting"
        @greetingRemoved="removeUnavailGreeting"
      />
    </div>

    <div id="busy-greeting-container" class="mt-20">
      <h2 class="form-section-header mb-0">Busy Greeting</h2>
      <el-tag class="mt-5">
        <i class="el-icon-info" style="margin-right: 4px" />
        This plays when you are on the phone
      </el-tag>
      <voicemail-greeting
        :mailboxuser="mailboxuser"
        :mailboxContext="mailboxContext"
        :currentGreeting="busyGreeting"
        defaultName="busy_greeting.wav"
        recordedName="busy_greeting.webm"
        dirFolder="busy"
        :mediafileOptions="mediafileOptions"
        @greetingSaved="updateBusyGreeting"
        @greetingRemoved="removeBusyGreeting"
      />
    </div>

    <div id="temporary-greeting-container" class="mt-20">
      <h2 class="form-section-header">Out of Office Greeting</h2>
      <el-tag class="mt-5">
        <i class="el-icon-info" style="margin-right: 4px" />
        This overrides your unavailable and busy greetings
      </el-tag>
      <div>
        <el-checkbox
          v-model="tempEnabled"
          v-if="tempGreeting"
          style="display: inline-block; margin-left: 0;"
        >
          Enable out of office greeting
        </el-checkbox>
      </div>
      <voicemail-greeting
        :mailboxuser="mailboxuser"
        :mailboxContext="mailboxContext"
        :currentGreeting="tempGreeting"
        defaultName="out_of_office_greeting.wav"
        recordedName="out_of_office_greeting.webm"
        dirFolder="temporary"
        :mediafileOptions="mediafileOptions"
        @greetingSaved="updateTempGreeting"
        @greetingRemoved="removeTempGreeting"
      />
    </div>
  </div>
</template>

<script>
import feathersClient from "@/feathers/feathers-client";
import voicemailGreeting from "./voicemail-greeting";
import debounce from "lodash/debounce";

export default {
  name: "VoicemailSettings",

  props: {
    mailboxuser: { required: true },
    mailboxContext: { required: true },
    mediafileOptions: { required: true },
  },

  components: { voicemailGreeting },

  data() {
    return {
      nameGreeting: null,
      busyGreeting: null,
      unavailGreeting: null,
      tempGreeting: null,
      tempEnabled: false,
      greetingAudioElement: null,
      myVoicemail: null,
    };
  },
  computed: {
    user() {
      return this.$store.state.auth.user;
    },
  },
  watch: {
    tempEnabled: debounce(function () {
      if (this.tempGreeting) {
        this.toggleTempGreeting();
      }
    }, 500),

    mailboxuser: {
      immediate: true,

      handler(val) {
        if (val && this.mailboxContext) {
          this.queryGreetings();
        }
      },
    },
  },
  methods: {
    queryGreetings() {
      feathersClient
        .service("voicemail-messages")
        .find({
          query: {
            msgNum: -1,
            mailboxuser: this.mailboxuser,
            mailboxContext: this.mailboxContext,
          },
        })
        .then((res) => {
          //figure out which greetings are which
          for (let i = 0; i < res.length; i++) {
            const currentGreeting = res[i];
            const greetingSplit = currentGreeting.dir.split("/");
            const splitLength = greetingSplit.length;

            const vmType = greetingSplit[splitLength - 1];

            if (vmType === "unavail") {
              this.unavailGreeting = currentGreeting;
              continue;
            }
            if (vmType === "greet") {
              this.nameGreeting = currentGreeting;
              continue;
            }
            if (vmType === "temp" || vmType === "temp_deleted") {
              this.tempGreeting = currentGreeting;
              this.tempEnabled = vmType === "temp";
              continue;
            }
            if (vmType === "busy") {
              this.busyGreeting = currentGreeting;
            }
          }
        });
    },

    toggleTempGreeting() {
      const dirPath = this.tempEnabled ? "temp" : "temp_deleted";
      feathersClient.service("voicemail-messages").patch(this.tempGreeting.id, {
        dir: `/var/spool/asterisk/voicemail/${this.mailboxContext}/${this.mailboxuser}/${dirPath}`,
        mailboxuser: this.mailboxuser,
        mailboxContext: this.mailboxContext,
      });
    },
    updateNameGreeting(payload) {
      this.nameGreeting = payload;
    },
    updateBusyGreeting(payload) {
      this.busyGreeting = payload;
    },
    updateUnavailGreeting(payload) {
      this.unavailGreeting = payload;
    },
    updateTempGreeting(payload) {
      this.tempGreeting = payload;
      this.tempEnabled = true;
    },
    removeNameGreeting() {
      this.nameGreeting = null;
    },
    removeBusyGreeting() {
      this.busyGreeting = null;
    },
    removeUnavailGreeting() {
      this.unavailGreeting = null;
    },
    removeTempGreeting() {
      this.tempGreeting = null;
      this.tempEnabled = false;
    },
  },
};
</script>

<style lang="scss">
.vue-audio-recorder {
  width: 40px !important;
  height: 40px !important;
  background-color: #ef5350 !important;
}

.current-greeting-container {
  height: 35px;
}

#greeting-play-button {
  width: 55px;
  height: 55px;
  i {
    font-size: 2em;
  }
}
hr {
  opacity: 0.25;
}
</style>
